// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

// Add this check for environment
const isServer = typeof window == 'undefined';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Client-side: use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : process.env.REACT_APP_MARKETPLACE_ROOT_URL;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// Modify the request function to handle server-side environment
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : { body };

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    // headers: headers || { 'Content-Type': 'application/transit+json' },
    headers: {
      ...(!!headers
        ? Object.keys(headers).length > 1
          ? headers
          : {}
        : { 'Content-Type': 'application/transit+json' }),
    },
    ...bodyMaybe,
    ...rest,
  };

  // Use axios for server-side requests
  if (isServer) {
    const axios = require('axios');
    return axios({
      url,
      method: options.method || 'POST',
      data: bodyMaybe.body,
      headers: fetchOptions.headers,
    })
      .then(response => response.data)
      .catch(error => {
        if (error.response) {
          throw error.response.data;
        }
        throw error;
      });
  }

  // Separate response handling logic
  const handleResponse = res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  };

  // Client-side fetch
  return window.fetch(url, fetchOptions).then(handleResponse);
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, options = {}) => request(path, { ...options, method: methods.GET });

const patch = (path, body, options = {}) =>
  request(path, { ...options, method: methods.PATCH, body });

const put = (path, body, options = {}) => request(path, { ...options, method: methods.PUT, body });

const del = (path, options = {}) => request(path, { ...options, method: methods.DELETE });
const chatHistoryRequests = new Map();
const spaceMembersRequests = new Map();
const spaceDetailsRequests = new Map();
const spaceListRequests = new Map();
const usersWithBadgesRequests = new Map();
const currentCircleMemberRequests = new Map();
const circleAuthTokenRequests = new Map();
const allGptsRequests = new Map();
const favouriteGptRequests = new Map();
const trendingTopicsRequests = new Map();
let publishedHighlightsRequest = null;
const groupFolderRequests = new Map();
const groupPaymentRequests = new Map();
const loginTrackingRequests = new Map();
const youthAccountValidationRequests = new Map();
const stripeAccountLinkedRequests = new Map();

export const downloadModPaymentHistory = groupId => {
  const url = `${apiBaseUrl()}/api/downloadModPaymentHistory?groupId=${encodeURIComponent(
    groupId
  )}`;

  return window
    .fetch(url, {
      method: methods.GET,
      credentials: 'include',
      headers: { Accept: 'text/csv' },
    })
    .then(async response => {
      if (!response.ok) {
        const error = new Error('Failed to download moderator payment history');
        error.status = response.status;
        throw error;
      }

      return response.blob();
    });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

export const cartTransactionLineItems = body => {
  return post('/api/cart-transaction-line-items', body);
};
// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const verifyJwtToken = body => {
  return post('/api/verify-token', body);
};

export const generateJwtToken = body => {
  return post('/api/generate-token', body);
};

export const disconnectShopifyConnectionApi = body => {
  return post('/api/shopify/disconnect', body);
};

export const deleteClodinaryImage = body => {
  return post('/api/deleteImage', body);
};

export const getCategoryHierarchyApi = () => {
  return request('/api/admin/category-hierarchy', { method: methods.GET });
};

export const updateCategoryOptionsApi = body => {
  return post('/api/admin/category-options', body);
};

export const updateCategoryLabelApi = body => {
  return post('/api/admin/category-label', body);
};

export const createCategoryItemApi = body => {
  return post('/api/admin/category-item', body);
};

// Sync listing variants to MongoDB
// See `server/api/listing-variants.js` for endpoint details
export const syncListingVariants = body => {
  return post('/api/listing-variants/sync', body);
};

// Get listing variants from MongoDB
export const getListingVariants = listingId => {
  const options = {
    method: 'GET',
  };
  return request(`/api/listing-variants/${listingId}`, options);
};

// Update variant stock in MongoDB
export const updateVariantStock = body => {
  if (!body?.transactionId) {
    console.warn('[updateVariantStock] Skipping request because transactionId is missing', body);
    return Promise.resolve({
      success: false,
      skipped: true,
      message: 'transactionId is missing',
    });
  }

  return post('/api/listing-variants/update-stock', body);
};

// // Batch update variant stock for multiple items
// export const batchUpdateVariantStock = body => {
//   return post('/api/listing-variants/batch-update-stock', body);
// };

// Check if listing has pending transactions
export const checkListingPendingTransactions = listingId => {
  const options = {
    method: 'GET',
  };
  return request(`/api/listing-transactions/check-pending/${listingId}`, options);
};

export const getCircleAuthToken = body => {
  const { forceRefresh } = body || {};
  const requestKey = forceRefresh ? null : JSON.stringify({ circleAuthToken: true });
  const pendingRequest = requestKey && circleAuthTokenRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/circle-auth-token', {
    ...(forceRefresh ? { forceRefresh } : {}),
  }).finally(() => {
    if (requestKey) circleAuthTokenRequests.delete(requestKey);
  });

  if (requestKey) {
    circleAuthTokenRequests.set(requestKey, requestPromise);
  }

  return requestPromise;
};

export const checkCircleMemberExists = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = currentCircleMemberRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/check-circle-member-exists', body).finally(() => {
    currentCircleMemberRequests.delete(requestKey);
  });

  currentCircleMemberRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const getAuthToken = body => {
  return;
  // return post('/api/create-auth-token', body);
};
export const getCircleHomePageApi = body => {
  return post('/api/get-circle-homePage', body);
};

export const createSpacePostApi = body => {
  return post('/api/create-space-post', body);
};

export const batchFetchPollsApi = body => {
  return post('/api/polls/by-circle-posts', body);
};

export const submitPollVoteApi = (pollId, body) => {
  return post(`/api/polls/${encodeURIComponent(pollId)}/vote`, body);
};

export const closePollApi = pollId => {
  return post(`/api/polls/${encodeURIComponent(pollId)}/close`, {});
};

export const createLikeForPostApi = body => {
  return post('/api/create-like-for-post', body);
};

export const createDislikeForPostApi = body => {
  return post('/api/create-dislike-for-post', body);
};

export const createCommentForPostApi = body => {
  return post('/api/create-comment-for-post', body);
};

export const deleteCommentFromPostApi = body => {
  return post('/api/delete-comment-from-post', body);
};

export const deleteReplyFromCommentApi = body => {
  return post('/api/delete-reply-from-comment', body);
};

export const fetchCurrentCommunityMember = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = currentCircleMemberRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/get-current-community-member', body).finally(() => {
    currentCircleMemberRequests.delete(requestKey);
  });

  currentCircleMemberRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// export const getUserProtectedDataWithApi = body => {
//   return post('/api/get-user-protected-data', body);
// };

export const getPublicProfileUserSummaryApi = body => {
  return post('/api/public-profile/user-summary', body);
};

export const fetchSpaceListApi = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = spaceListRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/get-list-of-spaces', body).finally(() => {
    spaceListRequests.delete(requestKey);
  });

  spaceListRequests.set(requestKey, requestPromise);
  return requestPromise;
};
export const fetchSpaceListApiV2 = body => {
  return post('/api/get-list-of-spaces-v2', body);
};

export const redirectToCircleApi = body => {
  return post('/api/redirectToCircle', body);
};

export const createNewCircleUserApi = body => {
  const { password } = body || {};
  return post('/api/create-new-circle-user', {
    ...(password ? { password } : {}),
  });
};

export const syncUserToCircleApi = body => {
  return post('/api/sync-user-to-circle', body);
};

export const directUploadApi = body => {
  return post('/api/direct-upload', body);
};

export const updateCircleSpaceApi = body => {
  return post('/api/update-circle-space', body);
};

// export const createNewSpaceApi = body => {
//   return post('/api/create-new-space', body);
// };

export const joinSpaceByIdApi = body => {
  return post('/api/join-space-by-id', body);
};

// export const trendingTopicsApi = body => {
//   const requestKey = JSON.stringify(body || {});
//   const pendingRequest = trendingTopicsRequests.get(requestKey);

//   if (pendingRequest) {
//     return pendingRequest;
//   }

//   const requestPromise = post('/api/trending-topics', body).finally(() => {
//     trendingTopicsRequests.delete(requestKey);
//   });

//   trendingTopicsRequests.set(requestKey, requestPromise);
//   return requestPromise;
// };

export const leaveSpaceByIdApi = body => {
  return post('/api/leave-space-by-id', body);
};

export const createBookMarkApi = body => {
  return post('/api/create-bookmark', body);
};

export const deleteBookMarkApi = body => {
  return post('/api/delete-bookmark', body);
};

export const fetchBookMarkApi = body => {
  return post('/api/fetch-bookmark', body);
};

export const getSinglePostApi = body => {
  return post('/api/get-single-post', body);
};

export const getCommentListForPostApi = body => {
  return post('/api/get-comment-list-for-post', body);
};

export const createLikeDislikeCommentApi = body => {
  return post('/api/create-like-dislike-comment', body);
};

export const getListOfReplyForCommentApi = body => {
  return post('/api/get-list-of-reply-for-comment', body);
};

export const createReplyForACommentApi = body => {
  return post('/api/create-reply-for-a-comment', body);
};

// export const getSubscriptionPrice = body => {
//   return post('/api/get-subscription-price', body);
// };

export const createStripeSubscription = body => {
  return post('/api/stripe-create-subscription', body);
};

export const getStripeSubscriptionStatus = body => {
  return post('/api/stripe-user-subscription-status', body);
};

export const onCancelStripeSubscription = body => {
  const { id } = body || {};
  return post('/api/cancel-stripe-subscription', { id });
};

export const updateSubscriptionOrganization = body => {
  return post('/app/ios/orgupdate', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
};


export const validateStripeCoupon = body => {
  return post('/api/validate-stripe-coupon', body);
};

export const fetchAdminDiscountCodesApi = () => {
  return get('/api/admin/discount-codes');
};

export const createAdminDiscountCodeApi = body => {
  return post('/api/admin/discount-codes', body);
};

export const updateAdminDiscountCodeApi = (couponId, body) => {
  return patch(`/api/admin/discount-codes/${encodeURIComponent(couponId)}`, body);
};

export const deleteAdminDiscountCodeApi = couponId => {
  return del(`/api/admin/discount-codes/${encodeURIComponent(couponId)}`);
};

export const fetchAdminDailyDealsCompaniesApi = params => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : '';
  return get(`/api/admin/daily-deals/companies${query}`);
};

export const createAdminDailyDealsCompanyApi = body => {
  return post('/api/admin/daily-deals/companies', body);
};

export const updateAdminDailyDealsCompanyApi = (companyId, body) => {
  return put(`/api/admin/daily-deals/companies/${encodeURIComponent(companyId)}`, body);
};

export const deleteAdminDailyDealsCompanyApi = companyId => {
  return del(`/api/admin/daily-deals/companies/${encodeURIComponent(companyId)}`);
};

export const fetchAdminDailyDealsDiscountsApi = params => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : '';
  return get(`/api/admin/daily-deals/discounts${query}`);
};

export const createAdminDailyDealsDiscountApi = body => {
  return post('/api/admin/daily-deals/discounts', body);
};

export const updateAdminDailyDealsDiscountApi = (discountId, body) => {
  return put(`/api/admin/daily-deals/discounts/${encodeURIComponent(discountId)}`, body);
};

export const deleteAdminDailyDealsDiscountApi = discountId => {
  return del(`/api/admin/daily-deals/discounts/${encodeURIComponent(discountId)}`);
};

export const fetchAdminSubscriptionsApi = params => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : '';
  return get(`/api/admin/subscriptions${query}`);
};

export const fetchAdminSubscriptionHistoryApi = params => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : '';
  return get(`/api/admin/subscriptions/history${query}`);
};

export const updateAdminSubscriptionOrganizationApi = body => {
  return put('/api/admin/subscriptions/organization', body);
};

export const fetchPublicDailyDealsApi = params => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : '';
  return get(`/api/daily-deals/discounts${query}`);
};
export const getSpaceDetailsApi = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = spaceDetailsRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/get-space-details', body).finally(() => {
    spaceDetailsRequests.delete(requestKey);
  });

  spaceDetailsRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const getSpaceTopicDetailApi = body => {
  return post('/api/get-space-topic-detail', body);
};

export const getSpaceMembersApi = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = spaceMembersRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/get-space-members', body).finally(() => {
    spaceMembersRequests.delete(requestKey);
  });

  spaceMembersRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const removeSpaceMemberApi = body => {
  return post('/api/remove-space-member', body);
};

export const deleteSpaceApi = body => {
  return post('/api/delete-space', body);
};

export const getBookMarksApi = body => {
  return post('/api/get-bookmarks', body);
};

export const uploadToR2 = body => {
  const options = {
    headers: {
      // 'Content-Type': 'multipart/form-data',
      // browser should set this header automatically when using FormData. Manually setting this header can cause the "Multipart: Boundary not found" error.
    },
  };
  return post('/api/cloudfare/upload-file-to-r2', body, options);
};

export const updateCurrentCircleUserApi = body => {
  return post('/api/update-current-circle-user', body);
};

export const advancedSearchApi = body => {
  return post('/api/advanced-search', body);
};

export const editCommentApi = body => {
  return post('/api/edit-comment-for-post', body);
};

export const editReplyCommentApi = body => {
  return post('/api/edit-reply-from-comment', body);
};

export const editCirclePostApi = body => {
  const postId = body?.postId;
  return postId
    ? put(`/api/community/posts/${postId}/edit`, body)
    : post('/api/edit-circle-post', body);
};

export const deleteCirclePostApi = body => {
  return post('/api/delete-circle-post', body);
};

export const followCirclePostApi = body => {
  return post('/api/follow-circle-post', body);
};

export const flaggedContentApi = body => {
  return post('/api/flagged-content', body);
};

export const getSpaceTopicsApi = body => {
  return post('/api/get-space-topics', body);
};

export const searchGroupsApi = body => {
  return post('/api/search-groups', body);
};

export const searchSpaceGroupsApi = body => {
  return post('/api/search-space-groups', body);
};

export const getNotificationsListApi = body => {
  return post('/api/get-notifications-list', body);
};

export const getNotificationsCountApi = body => {
  return post('/api/get-new-notification-counts', body);
};

export const markNotificationAsReadApi = body => {
  return post('/api/mark-notifications-as-read', body);
};

export const resetNotificationCountApi = body => {
  return post('/api/reset-notification-count', body);
};

export const notificationPreferencesApi = body => {
  return post('/api/notification-preferences', body);
};

export const getNotificationPrefernceApi = body => {
  return post('/api/get-notification-preferences', body);
};

export const getMemberDetailApi = body => {
  return post('/api/get-member-detail', body);
};

export const getMediaTranscodingStatusApi = body => {
  return post('/api/get-media-transcoding-status', body);
};

export const markAllNotificationsAsReadApi = body => {
  return post('/api/mark-all-notifications-as-read', body);
};

export const addMemberToSpaceApi = body => {
  return post('/api/add-member-to-space', body);
};

export const findMemberToSpaceApi = body => {
  return post('/api/find-member-to-space', body);
};

// send grid apis
export const onSingleMailApiCollection = body => {
  return post('/api/singleMailApiCollection', body);
};

export const onMultiMailApiCollection = body => {
  return post('/api/multiMailApiCollection', body);
};

export const updateUserApi = body => post('/api/updateUser', body);

export const adminUpdateUserApi = body => post('/api/admin/updateUser', body);

export const upsertUserDetailsApi = body => post('/api/upsert-user-details', body);

export const findUserDetailsApi = body => post('/api/find-user-details', body);

export const checkPhoneAvailabilityApi = body => post('/api/check-phone-availability', body);

// Twilio SMS verification APIs
export const sendOTPApi = body => post('/api/twilio/send-otp', body);
export const verifyOTPApi = body => post('/api/twilio/verify-otp', body);

// mongo fetch data
export const onUpdateCategoryCollection = body => {
  return post('/api/updateCategoryCollection', body);
};

export const onFindCategoryCollection = body => {
  return post('/api/findCategoryCollection', body);
};
export const onGroupJoinRequestCollection = body => {
  return post('/api/groupJoinRequestCollection', body);
};

export const onFindGroupJoinRequestCollection = body => {
  return post('/api/findGroupJoinRequestCollection', body);
};
export const onGetGroupJoinRequest = body => {
  return post('/api/getGroupJoinRequest', body);
};
export const onDeclineGroupJoinRequest = body => {
  return post('/api/declineGroupJoinRequest', body);
};

export const groupPostSearchApi = body => {
  return post('/api/group-post-search', body);
};

export const fetchUserActivityApi = body => {
  return post('/api/fetch-user-activity', body);
};

export const fetchPublicProfileCircleDataApi = body => {
  return post('/api/public-profile/circle-data', body);
};

// <<<<<<<< algolia apiType check in types.js >>>>>>>>>>>>>>>>
export const algoliaCRUDApis = (apiType, body) => {
  return post(`/api/${apiType}`, body);
};

// shippo apis

export const getShippingRates = body => {
  return post('/api/fetchShippingRates', body);
};

export const createShippingLabel = body => {
  return post('/api/createShippingLabel', body);
};

export const shippoWebhook = body => {
  return post('/api/trackShippoOrder', body);
};

export const validatingAddressWithShippo = body => {
  return post('/api/validatingAddress', body);
};

export const getCarrierAccount = body => {
  return post('/api/getCarriers', body);
};

// create folder for groups
export const onGroupFolderCollectionCreate = body => {
  return post('/api/groupFolderCollectionCreate', body);
};
export const onGroupFolderCollectionFind = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = groupFolderRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/groupFolderCollectionFind', body).finally(() => {
    groupFolderRequests.delete(requestKey);
  });

  groupFolderRequests.set(requestKey, requestPromise);
  return requestPromise;
};
export const onGroupFolderCollectionUpdate = body => {
  return post('/api/groupFolderCollectionUpdate', body);
};

// create documnets for folders
export const onGroupFolderDocumentCollectionCreate = body => {
  return post('/api/groupFolderDocumentCollectionCreate', body);
};
export const onGroupFolderDocumentCollectionFind = body => {
  return post('/api/groupFolderDocumentCollectionFind', body);
};
export const onGroupFolderDocumentCollectionUpdate = body => {
  return post('/api/groupFolderDocumentCollectionUpdate', body);
};
// integration apis

export const validateYouthAccount = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = youthAccountValidationRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/validateYouthAccount', body).finally(() => {
    youthAccountValidationRequests.delete(requestKey);
  });

  youthAccountValidationRequests.set(requestKey, requestPromise);
  return requestPromise;
};
// fetch user data
// export const fetchUser = body => {
//   return post('/api/fetchUserData', body);
// };
// organizationCollectionFind
export const onOrganizationCollectionFind = body => {
  return post('/api/organizationCollectionFind', body);
};
export const onOrganizationCollectionUpdate = body => {
  return post('/api/organizationCollectionUpdate', body);
};
export const onOrganizationCollectionCreate = body => {
  return post('/api/organizationCollectionCreate', body);
};

// open ai apis

export const getSpecificUserGPTS = body => {
  return post('/api/fetch-users-gpts', body);
};

export const chatUserWithGPTS = body => {
  return post('/api/chat-with-gpts', body);
};

export const chatEvaluateWithGPTS = body => {
  return post('/api/chat-evaluate', body);
};

export const fetchAllGpts = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = allGptsRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/fetch-all-gpts', body).finally(() => {
    allGptsRequests.delete(requestKey);
  });

  allGptsRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const fetchChatHistory = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = chatHistoryRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/fetch-chat-history', body).finally(() => {
    chatHistoryRequests.delete(requestKey);
  });

  chatHistoryRequests.set(requestKey, requestPromise);
  return requestPromise;
};
export const deleteRulebookApi = body => {
  return post('/api/delete-rule-book', body);
};
export const addOrRemoveFavouriteGpt = body => {
  return post('/api/add-remove-fav-gpt', body);
};
export const getFavouriteGpt = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = favouriteGptRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/get-fav-gpt', body).finally(() => {
    favouriteGptRequests.delete(requestKey);
  });

  favouriteGptRequests.set(requestKey, requestPromise);
  return requestPromise;
};
export const fetchAllGptsWithPage = body => {
  return post('/api/get-all-gpts-page', body);
};

export const addNewRulebook = formData => {
  const options = {
    headers: {
      // Content-Type will be set automatically by the browser for FormData
    },
  };
  return post('/api/add-rule-book', formData, options);
};

export const updateExistingRulebook = formData => {
  const options = {
    headers: {
      // Content-Type will be set automatically by the browser for FormData
    },
  };
  return post('/api/update-rule-book', formData, options);
};

export const deleteRuleBookFile = body => {
  return post('/api/delete-rule-book-file', body);
};

export const retryRulebookIndexingApi = body => {
  return post('/api/retry-rulebook-indexing', body);
};

export const getRulebookIndexingStatusApi = body => {
  return post('/api/rulebook-indexing-status', body);
};

export const getAllRulebooks = body => {
  return post('/api/get-all-rulebooks', body);
};

export const fetchListOfSpacesbyEmail = body => {
  return post('/api/fetch-group-list', body);
};

// youth Account apis
export const createYouthAccountApi = body => {
  return post('/api/create-youth-account', body);
};

export const findYouthAccountApi = body => {
  return post('/api/find-youth-account', body);
};

export const updateYouthAccountApi = body => {
  return post('/api/update-youth-account', body);
};

export const onCheckEmailPresent = body => {
  return post('/api/check-email-present', body);
};

// stripe
export const createStripeTransfer = body => {
  return post('/api/create-transfer', body);
};

export const fetchTransactionApi = body => {
  return post('/api/update-mongo-transaction', body);
};

// add free transaction entry in mongo
export const freeTransactionCreate = body => {
  return post('/api/free-transaction-create', body);
};

// /check-user-stripe-account-linked
export const onCheckUserStripeAccountLinked = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = stripeAccountLinkedRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/check-user-stripe-account-linked', body).finally(() => {
    stripeAccountLinkedRequests.delete(requestKey);
  });

  stripeAccountLinkedRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// fetchFreeTransactions
export const fetchFreeTransactions = body => {
  return post('/api/freeTransactionFind', body);
};

export const onFetchSpaceMembeEmails = body => {
  return post('/api/fetchSpaceMembeEmails', body);
};

// Public community endpoints
export const fetchPublicPostsApi = body => {
  return post('/api/fetch-public-posts', body);
};

export const fetchPublicCommentsApi = body => {
  return post('/api/fetch-public-comments', body);
};

export const fetchPublicSpaceApi = body => {
  return post('/api/fetch-public-space', body);
};

// prompts api

export const addRulebookPromptsApi = body => {
  return post('/api/add-prompts', body);
};

export const getRulebookPromptsApi = body => {
  return post('/api/get-prompts', body);
};

export const deletePromptByIdApi = body => {
  return post('/api/delete-prompts', body);
};

export const getChatHistoryByRulebookIdApi = body => {
  return post('/api/get-chat-history-by-id', body);
};

// Intercom Apis
export const onSendEventIntercom = body => {
  return post('/api/send-event-intercom', body);
};

export const onCreateIntercomTicket = body => {
  return post('/api/create-intercom-ticket', body);
};

// moderator apis
export const onGetModeratorCreateAndUpdateCollection = body => {
  return post('/api/moderatorCreateAndUpdateCollection', body);
};

export const onGetModeratorFindCollection = body => {
  return post('/api/moderatorFindCollection', body);
};

export const onModeratorRemoveFromGroup = body => {
  return post('/api/moderatorRemoveFromGroup', body);
};

export const onGroupAboutCollectionUpsert = body => {
  return post('/api/groupAboutCollectionUpsert', body);
};

export const onGroupAboutCollectionFind = body => {
  return post('/api/groupAboutCollectionFind', body);
};

export const syncCircleSpacesMemberCountsApi = body => {
  return post('/api/admin/sync-circle-spaces-member-counts', body);
};

// signup check
export const onElephantTrustSignupCheck = body => {
  return post('/api/elephantTrustSignupCheck', body);
};

// group payments
export const onGroupPaymentCollectionCreate = body => {
  return post('/api/groupPaymentCollectionCreate', body);
};
export const onGroupPaymentCollectionFind = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = groupPaymentRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/groupPaymentCollectionFind', body).finally(() => {
    groupPaymentRequests.delete(requestKey);
  });

  groupPaymentRequests.set(requestKey, requestPromise);
  return requestPromise;
};
export const onGroupPaymentCollectionUpdate = body => {
  return post('/api/groupPaymentCollectionUpdate', body);
};

export const onStripeGroupSubscriptionPayment = body => {
  return post('/api/stripe-group-subscription', body);
};

export const onCreateStripePaymentIntent = body => {
  return post('/api/createStripePaymentIntent', body);
};

export const onCustomerGroupPaymentCollectionCreate = body => {
  return post('/api/customerGroupPaymentCollectionCreate', body);
};

// customerGroupPaymentCollectionFind
export const onCustomerGroupPaymentCollectionFind = body => {
  return post('/api/customerGroupPaymentCollectionFind', body);
};

export const onCustomerGroupPaymentInvoiceCollectionCreate = body => {
  return post('/api/customerGroupPaymentInvoiceCollectionCreate', body);
};

export const onCustomerGroupPaymentInvoiceCollectionFind = body => {
  return post('/api/customerGroupPaymentInvoiceCollectionFind', body);
};

export const onCustomerGroupPaymentCollectionCancel = body => {
  return post('/api/customerGroupPaymentCollectionCancel', body);
};

// export const onFetchAllUsers = body => {
//   return post('/api/fetch-all-users', body);
// };

export const onCheckUserAlreadyExistApi = body => {
  return post('/api/check-user-already-exist', body);
};

export const onSendElephantFeedBack = body => {
  return post('/api/elephantFeedBack', body);
};

// fetchGhostPosts
export const onFetchGhostPosts = body => {
  return post('/api/fetchGhostPosts', body);
};

// elephant collection
export const onFetchElephantUserFind = body => {
  return post('/api/elephantUserFind', body);
};

export const onUpdateElephantUser = body => {
  return post('/api/elephantUserUpdate', body);
};

export const onTrackElephantUserEmailUpdate = body => {
  return post('/api/elephantUserTrackEmailUpdate', body);
};

export const onSendGroupPaymnetMailApiCollection = body => {
  return post('/api/sendGroupPaymnetMailApiCollection', body);
};

export const onCreatePushNotification = body => {
  return post('/api/createPushNotification', body);
};

export const sendInquiryNotification = body => {
  return post('/api/sendInquiryNotification', body);
};

export const onDeleteUser = body => {
  return post('/api/deleteUser', body);
};

// My Feed Highlights admin APIs
export const getAdminHighlightsApi = () => get('/api/admin/my-feed-highlights');

export const createHighlightCategoryApi = body => post('/api/admin/highlight-categories', body);

export const renameHighlightCategoryApi = (categoryId, body) =>
  patch(`/api/admin/highlight-categories/${categoryId}/name`, body);

export const reorderHighlightCategoriesApi = body =>
  patch('/api/admin/highlight-categories/reorder', body);

export const publishHighlightCategoryApi = (categoryId, body) =>
  patch(`/api/admin/highlight-categories/${categoryId}/publication`, body);

export const deleteHighlightCategoryApi = categoryId =>
  del(`/api/admin/highlight-categories/${categoryId}`);

export const createHighlightSlideApi = body => post('/api/admin/highlight-slides', body);

export const updateHighlightSlideApi = (slideId, body) =>
  patch(`/api/admin/highlight-slides/${slideId}`, body);

export const reorderHighlightSlidesApi = body => patch('/api/admin/highlight-slides/reorder', body);

export const deleteHighlightSlideApi = slideId => del(`/api/admin/highlight-slides/${slideId}`);

// Discover Groups carousel admin APIs
export const getAdminDiscoverGroupsCarouselsApi = () =>
  get('/api/admin/discover-groups-carousels');

export const createDiscoverGroupsCarouselApi = body =>
  post('/api/admin/discover-groups-carousels', body);

export const updateDiscoverGroupsCarouselApi = (carouselId, body) =>
  patch(`/api/admin/discover-groups-carousels/${carouselId}`, body);

export const reorderDiscoverGroupsCarouselsApi = body =>
  patch('/api/admin/discover-groups-carousels/reorder', body);

export const deleteDiscoverGroupsCarouselApi = carouselId =>
  del(`/api/admin/discover-groups-carousels/${carouselId}`);

export const addDiscoverGroupsCarouselGroupApi = (carouselId, body) =>
  post(`/api/admin/discover-groups-carousels/${carouselId}/groups`, body);

export const reorderDiscoverGroupsCarouselGroupsApi = (carouselId, body) =>
  patch(`/api/admin/discover-groups-carousels/${carouselId}/groups/reorder`, body);

export const updateDiscoverGroupsCarouselGroupApi = (groupId, body) =>
  patch(`/api/admin/discover-groups-carousel-groups/${groupId}`, body);

export const deleteDiscoverGroupsCarouselGroupApi = groupId =>
  del(`/api/admin/discover-groups-carousel-groups/${groupId}`);

export const getPublishedDiscoverGroupsCarouselsApi = () =>
  get('/api/discover-groups-carousels');

// My Feed Highlights member API
export const getPublishedHighlightsApi = () => {
  if (publishedHighlightsRequest) {
    return publishedHighlightsRequest;
  }

  publishedHighlightsRequest = get('/api/my-feed-highlights').finally(() => {
    publishedHighlightsRequest = null;
  });

  return publishedHighlightsRequest;
};

export const markHighlightSlideSeenApi = body => post('/api/my-feed-highlights/seen', body);

export const trackHighlightCtaClickApi = body => post('/api/my-feed-highlights/cta-clicks', body);

export const onDeleteSharetribeUser = body => {
  return post('/api/deleteSharetribeUser', body);
};

// Fetch subcategory filters from Algolia
export const getSubCategoryFilters = body => {
  return post('/api/getSubCategoryFilters', body);
};

// fetch user data
export const fetchUserApi = body => {
  return post('/api/fetch-user-data', body);
};

export const fetchUserBadgesApi = body => {
  return post('/api/badges/earned', body);
};

export const trackUserLoginApi = body => {
  const requestKey = JSON.stringify(body || {});
  const pendingRequest = loginTrackingRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/badges/track-login', body).finally(() => {
    loginTrackingRequests.delete(requestKey);
  });

  loginTrackingRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const fetchUsersWithBadgesApi = body => {
  const userIds = Array.isArray(body?.userIds)
    ? [...new Set(body.userIds.map(id => (id == null ? '' : String(id).trim())).filter(Boolean))]
    : [];
  const normalizedBody = { ...body, userIds };
  const requestKey = JSON.stringify({ ...normalizedBody, userIds: [...userIds].sort() });
  const pendingRequest = usersWithBadgesRequests.get(requestKey);

  if (pendingRequest) {
    return pendingRequest;
  }

  const requestPromise = post('/api/badges/users-with-badges', normalizedBody).finally(() => {
    usersWithBadgesRequests.delete(requestKey);
  });

  usersWithBadgesRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const fetchUsersSubscriptionStatusApi = body => {
  return post('/api/users-subscription-status', body);
};

// User Stripe Connection
export const updateUserStripeConnectionApi = body => {
  return post('/api/userStripeConnectionUpsert', body);
};

export const getUserStripeConnectionApi = body => {
  return post('/api/userStripeConnectionFind', body);
};

export const getUsersStripeConnectionApi = body => {
  return post('/api/usersStripeConnectionFind', body);
};

// Update transaction metadata for message read tracking
export const updateTransactionMetadata = body => {
  return post('/api/update-transaction-metadata', body);
};

// Fetch transactions with metadata for inbox unread count
export const fetchTransactionsWithMetadata = (userId, role = 'all') => {
  const params = new URLSearchParams({
    userId,
    role,
  });

  return request(`/api/fetch-transactions-with-metadata?${params.toString()}`, {
    method: methods.GET,
  });
};

// ─── Shopify Onboarding ───────────────────────────────────────────────────────

export const getShopifyProductTypesApi = body => post('/api/shopify/product-types', body);

export const getShopifyProductsApi = body => post('/api/shopify/products/list', body);

export const importShopifyProductsApi = body => post('/api/shopify/products/import', body);

export const upsertCategoryMappingApi = body => post('/api/shopify/category-mappings/upsert', body);

export const getCategoryMappingsApi = body => post('/api/shopify/category-mappings/list', body);

// ─── Taxonomy-based mapping (keyed by Shopify taxonomy GID) ──────────────────

export const getTaxonomyNodesApi = body => post('/api/shopify/taxonomy/nodes', body);

export const upsertTaxonomyMappingApi = body => post('/api/shopify/taxonomy-mappings/upsert', body);

export const getTaxonomyMappingsApi = body => post('/api/shopify/taxonomy-mappings/list', body);

export const upsertAttributeMappingsApi = body =>
  post('/api/shopify/attribute-mappings/upsert', body);

export const getAttributeMappingsApi = body => post('/api/shopify/attribute-mappings/list', body);

export const toggleShopifyProductImportApi = body =>
  post('/api/shopify/products/toggle-import', body);

export const recordShopifyFeeEventApi = body => post('/api/shopify/fees/record', body);

export const getShopifyFeeReportApi = body => post('/api/shopify/fees/list', body);

export const getShopifyBillingStatusApi = body => post('/api/shopify/billing/status', body);

export const onSendOutOfStockMailProviders = body => {
  if (!body?.transactionId && !body?.orderId) {
    console.warn('[onSendOutOfStockMailProviders] Skipping request because transactionId is missing', body);
    return Promise.resolve({
      success: false,
      skipped: true,
      message: 'transactionId is missing',
    });
  }

  return post('/api/mail/sendOutOfStockMailProviders', body);
};

export const getGroupHighlightsApi = body => post('/api/get-group-highlights', body);
export const pinGroupHighlightApi = body => post('/api/pin-group-highlight', body);
export const unpinGroupHighlightApi = body => post('/api/unpin-group-highlight', body);
export const getGroupMutePreferenceApi = body => post('/api/get-group-mute-preference', body);
export const setGroupMutePreferenceApi = body => post('/api/set-group-mute-preference', body);
